// Patch 008.6 → 008.7 — header chassis with inline centered nav.
//
// Layout (desktop, 3-column grid):
//   left:   logo + wordmark + project switcher
//   center: 7-section nav (Today | Plan | Build | Money | People | Marketing | Ops)
//   right:  + Invite + ↓ Export
//
// Mobile (≤720px): the grid collapses to one column. Wordmark + inline
// nav hide; the mobile <select> nav surfaces. Logo, switcher, and
// action buttons stay.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DeleteProjectModal } from './DeleteProjectModal'
import { Nav, NavMobile, type SectionId } from './Nav'
import { NewProjectModal } from './NewProjectModal'
import {
  ProjectSwitcher,
  getNextProjectId,
  refreshProjectList,
  useCurrentProject,
} from './ProjectSwitcher'
import { UserChip } from './UserChip'

export function Header(props: {
  projectId?: string
  activeSection: SectionId | null
}): JSX.Element {
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false)
  const navigate = useNavigate()
  // Patch 010.0.4 — current project metadata for the Delete button's
  // disabled state + the modal's display_name + address_street.
  const currentProject = useCurrentProject(props.projectId)
  const deleteDisabled =
    !props.projectId || !currentProject || currentProject.is_synthetic === 1
  return (
    <header className="hcc-header" data-testid="app-header">
      <div className="hcc-header__left">
        <div className="hcc-brand">
          <img
            className="hcc-brand__logo"
            // Patch 009.5 — swap to 256×256 WebP (q85, ~8.5 KB) from
            // the 1254×1254 PNG (~2.2 MB). Display size is 96/60/48px
            // so the 256×256 source covers 2x retina at every breakpoint.
            // Original PNG kept in /public for any future full-resolution
            // surface (marketing, auth page, etc.).
            // Cache-bust string bumped from ?v=hh-2026-05-08 (009.3) to
            // ?v=hh-webp-2026-05-09 so Cloudflare edge + browser caches
            // re-fetch the new asset.
            src="/logo.webp?v=hh-webp-2026-05-09"
            alt="Heritage Homes"
            data-testid="brand-logo"
          />
          <div className="hcc-brand__text">
            <div className="hcc-brand__name">Heritage Homes</div>
            <div className="hcc-brand__sub">COMMAND CENTER</div>
          </div>
        </div>
        <ProjectSwitcher projectId={props.projectId} />
        <button
          type="button"
          className="hcc-header__add-project"
          onClick={() => setNewProjectOpen(true)}
          title="Add a new project"
          aria-label="Add a new project"
          data-testid="header-add-project"
        >
          + Add Project
        </button>
      </div>
      <div className="hcc-header__center">
        <Nav projectId={props.projectId} activeSection={props.activeSection} />
        <NavMobile projectId={props.projectId} activeSection={props.activeSection} />
      </div>
      <div className="hcc-header__right">
        <button
          type="button"
          className="hcc-header__action"
          onClick={() => {
            /* visual stub — wires in a later patch */
          }}
          title="Invite a client (deferred)"
          data-testid="header-invite"
        >
          + Invite
        </button>
        <button
          type="button"
          className="hcc-header__action"
          onClick={() => {
            /* visual stub — wires in a later patch */
          }}
          title="Export project as JSON (deferred)"
          data-testid="header-export"
        >
          ↓ Export
        </button>
        {/* Patch 010.0.4 — Delete project lives in the right slot
            (semantic: current-project action; sits with Invite + Export).
            Icon-only (✕) to keep the right-slot width-budget from
            pushing the centered nav into Add Project at 1920px (per
            header_no_overlap.spec.ts at 1920). The full destructive
            label is the modal title; the icon + tooltip carry the UI
            affordance. */}
        <button
          type="button"
          className="hcc-header__delete-project"
          onClick={() => setDeleteProjectOpen(true)}
          title={
            deleteDisabled
              ? currentProject?.is_synthetic === 1
                ? 'Delete project: Synthetic Variance Pilot is protected'
                : 'Delete project: select a project first'
              : `Delete project: ${currentProject?.display_name}`
          }
          aria-label="Delete project"
          disabled={deleteDisabled}
          data-testid="header-delete-project"
        >
          ✕
        </button>
        <UserChip />
      </div>
      <NewProjectModal
        open={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
        onCreated={(project) => {
          refreshProjectList()
          // Patch 015.3 — a new project lands on the setup wizard
          // (create project -> wizardized intake), not the Command Board.
          navigate(`/projects/${project.id}/setup`)
        }}
      />
      {props.projectId && currentProject ? (
        <DeleteProjectModal
          open={deleteProjectOpen}
          projectId={props.projectId}
          projectDisplayName={currentProject.display_name}
          projectAddressStreet={currentProject.address_street}
          onClose={() => setDeleteProjectOpen(false)}
          onDeleted={({ projectId }) => {
            setDeleteProjectOpen(false)
            const next = getNextProjectId(projectId)
            refreshProjectList()
            if (next) {
              navigate(`/projects/${next}/plan/scope`)
            } else {
              navigate('/')
            }
          }}
        />
      ) : null}
    </header>
  )
}
